import React, { useMemo } from 'react';

import { FunnelChart } from 'echarts/charts';
import * as echarts from 'echarts/core';

import { formatValue, memberLabel } from '../format';
import type { Palette } from '../palette';
import type { Envelope } from '../types';

import Chart, { type ChartOption } from './Chart';

echarts.use([FunnelChart]);

export default function Funnel({ envelope, palette }: { envelope: Envelope; palette: Palette }) {
  const option = useMemo<ChartOption>(() => {
    const dims = envelope.columns.filter((column) => column.role === 'dim' || column.role === 'time');
    const depth = dims.filter((column) => column.role === 'dim').length;
    const measures = envelope.columns.filter((column) => column.role === 'measure');
    const rows = dims.length ? envelope.rows.filter((row) => row.level === undefined || row.level === depth) : envelope.rows.slice(0, 1);
    const stages = rows.flatMap((row) => measures.flatMap((column) => {
      const value = row[column.name];
      const names = dims.map((dim) => memberLabel(String(row[dim.name] ?? '')));
      return typeof value === 'number' ? [{ name: [...names, ...(!dims.length || measures.length > 1 ? [column.label] : [])].join(' · '), value, format: column.format }] : [];
    }));
    const label = (item: { dataIndex: number; value: number; name: string }) => `${item.name}: ${formatValue(item.value, stages[item.dataIndex]?.format)}`;
    return {
      animation: false,
      textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
      tooltip: { backgroundColor: palette.surface, borderColor: palette.hairlineStrong, textStyle: { color: palette.ink }, formatter: label },
      series: [{
        type: 'funnel',
        left: 8,
        right: 150,
        top: 12,
        bottom: 12,
        sort: 'none',
        gap: 3,
        label: { position: 'right', color: palette.ink2, formatter: label },
        labelLine: { lineStyle: { color: palette.hairlineStrong } },
        data: stages.map((stage, index) => ({ name: stage.name, value: stage.value, itemStyle: { color: palette.series[index % palette.series.length] } })),
      }],
    };
  }, [envelope, palette]);
  return <Chart option={option} />;
}
